import React, { useState } from "react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Lock } from "lucide-react";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import { getStripe } from "@/lib/stripe";
import { useTheme } from "@/lib/theme";

function CardForm({ clientSecret, onSaved, onClose, fare, driverName }) {
  const stripe = useStripe();
  const elements = useElements();
  const { dark } = useTheme();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);
    try {
      const res = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (res.error) throw new Error(res.error.message);
      await onSaved(res.setupIntent.id);
    } catch (e) {
      setError(e.message || "Could not save your card.");
      setBusy(false);
    }
  };

  return (
    <div>
      {driverName && (
        <p className="mb-4 text-sm text-muted-foreground">
          Add a card to confirm {driverName}
          {fare ? ` · $${Number(fare).toFixed(2)} will be authorized` : ""}.
        </p>
      )}
      <div className="rounded-xl border border-border bg-secondary px-4 py-4">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: dark ? "#f4f4f5" : "#18181b",
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                "::placeholder": { color: dark ? "#8a8a93" : "#a1a1aa" },
              },
              invalid: { color: "#ef4444" },
            },
          }}
        />
      </div>
      {error && <p className="mt-3 text-sm font-medium text-destructive">{error}</p>}
      <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Lock className="h-3.5 w-3.5" /> Secured by Stripe.
      </div>
      <div className="mt-5 flex gap-3">
        <Button variant="secondary" className="flex-1" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button className="flex-1" onClick={submit} loading={busy} data-testid="save-card-btn">
          Save card
        </Button>
      </div>
    </div>
  );
}

export default function StripeCardModal({ open, clientSecret, onSaved, onClose, fare, driverName }) {
  return (
    <Modal open={open && !!clientSecret} onClose={onClose} title="Add a payment method" size="md">
      {clientSecret && (
        <Elements stripe={getStripe()} options={{ clientSecret }}>
          <CardForm
            clientSecret={clientSecret}
            onSaved={onSaved}
            onClose={onClose}
            fare={fare}
            driverName={driverName}
          />
        </Elements>
      )}
    </Modal>
  );
}
